import { Box, Button, Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { AutoAwesome } from '@mui/icons-material';
import { useRecoilValue } from 'recoil';
import {
    ContouringCustomizationFile,
    UserCustomization,
} from '~/backend/types';
import AvailableModels from '~/components/ModelSelection/AvailableModels';
import CustomizationSelection from '~/components/ModelSelection/CustomizationSelection';
import ModelParameters from '~/components/ModelSelection/ModelParameters';
import PseudonymizeCheckbox from '~/components/ModelSelection/PseudonymizeCheckbox';
import { Modality } from '~/domain/dicom/modality';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import StructureSelection from '../../contour/AutoSegmentation/StructureSelect';

type ContourPropagationModelSelectionProps = {
    modelCustomizations?: UserCustomization<ContouringCustomizationFile>[];
    availableModels?: PredictionModel[];
    modality?: Modality;
    disabled?: boolean;
    isLoadingModels?: boolean;
    isSubmitting?: boolean;
    showPseudonymization?: boolean;
    onSubmit: (
        model: PredictionModel,
        selectedStructures: string[],
        selectedCustomization?: string
    ) => void;
};

export default function ContourPropagationModelSelection({
    availableModels = [],
    disabled = false,
    isLoadingModels,
    isSubmitting,
    showPseudonymization,
    onSubmit,
}: ContourPropagationModelSelectionProps) {
    const [selectedModel, setSelectedModel] = useState<PredictionModel>();
    const [selectedCustomization, setSelectedCustomization] =
        useState<string>();
    const currentStructureSet = useRecoilValue(currentStructureSetState);

    const roiNames = useMemo(
        () => currentStructureSet?.roiArray.map((roi) => roi.name) || [],
        [currentStructureSet]
    );

    const [selectedStructures, setSelectedStructures] =
        useState<string[]>(roiNames);

    const customizations = useMemo(() => {
        return (
            selectedModel?.customizations?.filter(
                (customization) => customization.name !== selectedModel.name
            ) || []
        );
    }, [selectedModel]);

    const hasCustomizations = !!(selectedModel && customizations.length > 0);

    useEffect(() => {
        if (selectedModel) {
            setSelectedCustomization(undefined);
            setSelectedStructures(roiNames);
        }
    }, [selectedModel, roiNames]);

    return (
        <Box
            display="grid"
            gridTemplateRows="240px auto"
            gap={1}
            sx={{ transition: '0.6s' }}
        >
            <Box
                width="100%"
                height="100%"
                display="grid"
                gridTemplateColumns={
                    hasCustomizations ? '50% 25% 1fr' : '75% 1fr'
                }
                gap={1}
            >
                <AvailableModels
                    isLoading={isLoadingModels}
                    models={availableModels}
                    selectedModel={selectedModel}
                    setSelectedModel={setSelectedModel}
                />
                {hasCustomizations && (
                    <CustomizationSelection
                        customizations={customizations}
                        selectedCustomization={selectedCustomization}
                        setSelectedCustomization={setSelectedCustomization}
                    />
                )}
                <StructureSelection
                    selectedModel={selectedModel}
                    selectedStructures={selectedStructures}
                    roiNames={roiNames}
                    setSelectedStructures={setSelectedStructures}
                    workflowType={WorkflowTypes.ADAPT}
                />
            </Box>

            <Box>
                <Box
                    mt={0.5}
                    display="flex"
                    justifyContent="space-between"
                    gap={0.5}
                >
                    <ModelParameters
                        selectedModel={selectedModel}
                        selectedCustomization={selectedCustomization}
                        selectedStructures={selectedStructures}
                        roiNames={roiNames}
                    />
                    <Stack>
                        {showPseudonymization && <PseudonymizeCheckbox />}
                        <Button
                            onClick={() =>
                                selectedModel &&
                                onSubmit(
                                    selectedModel,
                                    selectedStructures,
                                    selectedCustomization
                                )
                            }
                            variant="outlined"
                            endIcon={<AutoAwesome />}
                            disabled={
                                !selectedModel ||
                                disabled ||
                                selectedStructures.length === 0 ||
                                isSubmitting
                            }
                            sx={{ width: 160 }}
                            data-testid="start-contour-propagation-button"
                        >
                            <FormattedMessage id="start" />
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
}
